import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Toggle } from 'Common/components/Atoms/Toggle';
import HeaderBar from '@containers/HeaderBar';
import { actions as accountActions } from '@reducers/Account';
import { actions as loginActions } from '@reducers/login';
import {
  accountErrorSelector,
  accountOrganizationsSelector,
  accountSelector,
  isLoadingAccountSelector,
  isUpdatingAccountSelector,
} from '../../selectors/Account';
import { useTheme } from '@mui/material/styles';
import { BodyCard } from 'Common/components/Molecules/BodyCard';
import { Loading } from 'Common/components/Atoms/Loading';
import { Button, Flex, InputField, Link } from 'Common';
import { Text } from 'Common/components/Atoms/Text';
import { popDest } from '@utils/intendedDest';

export function CompleteDeveloperProfile({
  user,
  updateProfile,
  fetchAccount,
  history,
  showCompanyFields,
  error,
  isUpdating,
  isLoading,
  ...props
}) {
  const theme = useTheme();
  const [values, setValues] = useState({
    name: '',
    terms_accepted: false,
    company: '',
    company_url: '',
  });
  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);
  useEffect(() => {
    if (user) {
      setValues({
        ...values,
        name: user.name || '',
        terms_accepted: user.terms_accepted || false,
        company: user.developer_profile.company || '',
        company_url: user.developer_profile.company_url || '',
      });
    }
    // eslint-disable-next-line
  }, [user]);
  const handleChange = (name) => (event) => {
    setValues({ ...values, [name]: event.target.value });
  };

  const handleSubmit = () => {
    const callback = () => {
      const uri = popDest() || '/dash';
      history.push(uri);
    };
    updateProfile({ ...values, callback });
  };

  const submitDisabled =
    !values.terms_accepted ||
    values.name.length === 0 ||
    (showCompanyFields &&
      (values.company.length === 0 || values.company_url.length === 0));

  return (
    <div>
      <HeaderBar {...props} />
      <Flex
        direction="column"
        width={454}
        m="auto"
        mt={15}
        alignItems="center"
        justifyContent="center"
      >
        <Text variant="h1" mb={6}>
          Complete Sign Up
        </Text>
        {user && !isLoading ? (
          <BodyCard>
            <InputField
              id="name"
              name="name"
              value={values.name}
              placeholder="Name"
              onChange={handleChange('name')}
              width="100%"
              label="User Name"
            />
            {showCompanyFields && (
              <React.Fragment>
                <InputField
                  id="company"
                  name="company"
                  value={values.company}
                  placeholder="Company Name"
                  onChange={handleChange('company')}
                  width="100%"
                  label="Company Name"
                  mt={2}
                />
                <InputField
                  id="company-url"
                  name="company_url"
                  value={values.company_url}
                  placeholder="Company URL"
                  onChange={handleChange('company_url')}
                  width="100%"
                  label="Company URL"
                  mt={2}
                />
              </React.Fragment>
            )}

            <label>
              <Flex alignItems="center" my={2}>
                <Toggle
                  checked={values.terms_accepted}
                  checkedIconText={''}
                  uncheckedIconText={''}
                  onChange={() => {
                    setValues({
                      ...values,
                      terms_accepted: !values.terms_accepted,
                    });
                  }}
                />
                <Text fontSize="body2" ml="10px">
                  {'I agree to the '}
                  <Link
                    fontSize="13px"
                    href="https://www.pullrequest.com/terms"
                    target="_blank"
                  >
                    Terms of Service
                  </Link>
                  {' and '}
                  <Link
                    fontSize="13px"
                    href="https://www.pullrequest.com/privacy"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </Text>
              </Flex>
            </label>

            {error && (
              <Text variant="body1" color="error" mt={2}>
                {error}
              </Text>
            )}
            <Button
              width="100%"
              disabled={submitDisabled}
              onClick={handleSubmit}
              variant="contained"
              color="primary"
            >
              {isUpdating ? (
                <Flex center justifyContent="center">
                  <Loading size={24} customcolor={theme.palette.common.white} />
                </Flex>
              ) : (
                values.submitTxt || 'Create Account'
              )}
            </Button>
          </BodyCard>
        ) : (
          <Loading size={64} />
        )}
      </Flex>
    </div>
  );
}

CompleteDeveloperProfile.defaultProps = {
  isLoading: true,
};

CompleteDeveloperProfile.propTypes = {
  user: PropTypes.object,
  updateProfile: PropTypes.func.isRequired,
  fetchAccount: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  showCompanyFields: PropTypes.bool,
  error: PropTypes.string,
  isUpdating: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
};

function mapStateToProps(state) {
  const account = accountSelector(state);
  const { user } = account;
  const organizations = accountOrganizationsSelector(state);
  const error = accountErrorSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  const isUpdating = isUpdatingAccountSelector(state);
  const showCompanyFields =
    !isLoadingAccount &&
    !organizations.length &&
    user &&
    !user.developer_profile.length;
  return {
    isUpdating,
    user,
    error,
    showCompanyFields,
    isLoading: isLoadingAccount,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...accountActions, ...loginActions }, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompleteDeveloperProfile);
